// Tape (CONTRACT "Tape", feature contract 2.3): 24 px on the floor step,
// "Latest observed" then the newest archive calls with 14 px artwork, the
// ticker 600, the name muted, the age, and the source the call came from.
// Collapsible with "t", remembered per destination class.
//
// The source is written, not only coloured. Feature contract 2.3 lists "token
// name, ticker, age, and source" as the item's fields, and a 7 px family dot
// alone made colour the sole carrier of it (A11Y-005). The dot stays as the
// scanning aid beside the written name.
//
// The strip is a real horizontal scroll box. Clipping it with overflow:hidden
// put roughly 16 of 23 items past the right edge with no way to reach them by
// pointer, and left the document's scroll area 4286 px wide at every viewport.
// Scrolling keeps every item reachable, keeps tab order working, and keeps the
// document at the viewport width.
//
// Collapsed means the grid row is 0 and this component renders nothing, per
// the motion table ("Tape toggle: grid row 24 to 0, 120 ms") and the approved
// chrome. The way back is the "t" key, the "?" sheet, and the status bar's
// "Latest observed" control, so the workspace never pays 24 px for a caret.
//
// Clicking an item opens that exact call in Live Calls (the selection travels
// in the URL hash, #call=<id>). Stale from coverage or a failed refresh is a
// warn label, never a hidden state.
import { useMemo } from 'react';
import { CaretUp } from '@phosphor-icons/react';
import { ARCHIVE_PAGE_LIMIT } from '../api/config';
import { useArchiveCalls } from '../api/hooks';
import { TokenArtwork } from '../identity/TokenArtwork';
import { useView } from '../router/useView';
import { stateOfError } from '../states/stateOf';
import { formatZurichTime, relativeAge } from '../time/zurich';
import { Button } from '../ui/Button';
import { PresenceDot } from '../ui/PresenceDot';
import { cx } from '../ui/cx';
import { callHash, writeCallHash } from './openCall';
import { useShell } from './ShellContext';
import { useNow } from './useNow';
import { tapeItemsOf, type TapeItem } from './tapeItems';

const TAPE_FILTERS = { limit: ARCHIVE_PAGE_LIMIT.tape } as const;
export const TAPE_ARTWORK_PX = 14;

function TapeRow({ item, now, onOpen }: { readonly item: TapeItem; readonly now: number; readonly onOpen: (id: string) => void }) {
  const age = relativeAge(item.at, now) ?? 'age unavailable';
  return (
    <button
      type="button"
      className={cx('tape-item', `tape-${item.kind}`)}
      title={`${item.primary}${item.secondary ? `, ${item.secondary}` : ''}, ${item.source}, ${formatZurichTime(item.at) ?? 'time unavailable'}`}
      onClick={() => onOpen(item.id)}
    >
      <TokenArtwork imageUrl={item.imageUrl} symbol={item.symbol} size={TAPE_ARTWORK_PX} />
      <b className={cx('tape-nm', item.mono && 'mono')}>{item.primary}</b>
      {item.secondary ? <span className="tape-tk">{item.secondary}</span> : null}
      <span className="tape-age num">{age}</span>
      {/* The family dot is the scanning aid; the written source beside it is
          what actually carries which source the call came from. */}
      <PresenceDot source={item.family} className="tape-src" />
      <span className="tape-from">{item.source}</span>
    </button>
  );
}

export function Tape() {
  const { tapeOpen, toggleTape } = useShell();
  const { view, navigate } = useView();
  const calls = useArchiveCalls(TAPE_FILTERS);
  // Derived once per answer. Built during render it ran again on every
  // shell state change (list, inspector, tape, help, toast) and on the 30 s
  // clock tick, mapping 30 rows each time for a strip that had not changed.
  const items = useMemo(() => tapeItemsOf(calls.rows), [calls.rows]);
  const now = useNow();
  const coverageStale = calls.coverage?.sourceHealth.state === 'stale';
  const staleReason = calls.stale ? stateOfError(calls.error).reason : coverageStale ? 'No source connector has a fresh heartbeat.' : null;

  // On Live Calls the click changes no destination, so the hash is written in
  // place. From anywhere else the call id is handed over as the destination's
  // whole hash, so this screen's own filters do not travel with it.
  const open = (id: string) => {
    if (view === 'live') {
      writeCallHash(id);
      return;
    }
    navigate('live', { hash: callHash(id) });
  };

  if (!tapeOpen) return null;

  return (
    <div className="tape" role="region" aria-label="Latest observed calls">
      <div className="tape-in">
        <Button variant="tertiary" size="sm" keyCap="t" icon={<CaretUp />} onClick={toggleTape}>Latest observed</Button>
        {staleReason ? <span className="tape-stale" title={staleReason}>stale</span> : null}
        {calls.isLoading ? <span className="tape-msg">Loading calls</span> : null}
        {!calls.isLoading && calls.error && items.length === 0 ? <span className="tape-msg">Calls unavailable: {stateOfError(calls.error).reason}</span> : null}
        {!calls.isLoading && !calls.error && items.length === 0 ? <span className="tape-msg">No calls observed in the retained window</span> : null}
        <div className="tape-items">
          {items.map((item) => <TapeRow key={item.id} item={item} now={now} onOpen={open} />)}
        </div>
        {/* The strip holds about seven items at 1440; the rest are a sideways
            scroll away, so the count and the way to all of them are printed
            where a pointer can see them. On Live Calls the stream already is
            this list, so the control would lead nowhere new. */}
        {view !== 'live' && items.length > 0 ? (
          <Button className="tape-all" variant="tertiary" size="sm" onClick={() => navigate('live')}>
            {`All ${items.length} in Live Calls`}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
